import React, { useMemo } from "react";
import { alpha, Autocomplete, Box, TextField, useTheme } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { NarrativeTemplate } from "../../../shared/interfaces/ReportGenReport";

interface Props {
  reportType: NarrativeTemplate;
  generalReportType: NarrativeTemplate;
  customReportTypes?: NarrativeTemplate[];
  onChange: (value: NarrativeTemplate) => void;
}

const ITEM_HEIGHT = 48;
const MAX_VISIBLE_ITEMS = 10;

export const ReportTypeSelector: React.FC<Props> = ({ reportType, generalReportType, customReportTypes, onChange }) => {
  const theme = useTheme();

  const options = useMemo(
    () => [generalReportType, ...(customReportTypes ?? [])],
    [generalReportType, customReportTypes]
  );

  return (
    <Autocomplete
      value={reportType}
      onChange={(_, value) => {
        if (value) onChange(value);
      }}
      options={options}
      getOptionLabel={(option) =>
        option.template.templateId === "default" ? "General Report" : option.template.name
      }
      isOptionEqualToValue={(option, value) => option.template.templateId === value.template.templateId}
      disableClearable
      ListboxProps={{
        style: { maxHeight: ITEM_HEIGHT * MAX_VISIBLE_ITEMS, overflow: "auto" },
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.template.templateId}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AssignmentIcon fontSize="small" color="primary" />
            {option.template.templateId === "default" ? "General Report" : option.template.name}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Report type"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(theme.palette.divider, 0.2) },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
            },
          }}
        />
      )}
    />
  );
};
